import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { url: String, debounceMs: { type: Number, default: 1000 } }
  static targets = ["status", "saveButton", "message"]

  connect() {
    this.pending = false
    this.dirty = false
    this._autosaveTimer = null
    this.element.dataset.consultoriaAutosaveConnected = "true"
    this.boundBeforeUnload = this.beforeUnload.bind(this)
    this.boundBeforeVisit = this.beforeVisit.bind(this)
    this.boundClickCapture = this.clickCapture.bind(this)
    this.boundAutosizeInput = this.autosizeField.bind(this)

    window.addEventListener("beforeunload", this.boundBeforeUnload)
    document.addEventListener("turbo:before-visit", this.boundBeforeVisit)
    document.addEventListener("click", this.boundClickCapture, true)
    this.setupTextareaAutosize()
  }

  disconnect() {
    clearTimeout(this._autosaveTimer)
    this.teardownTextareaAutosize()
    delete this.element.dataset.consultoriaAutosaveConnected
    window.removeEventListener("beforeunload", this.boundBeforeUnload)
    document.removeEventListener("turbo:before-visit", this.boundBeforeVisit)
    document.removeEventListener("click", this.boundClickCapture, true)
  }

  markDirty() {
    this.dirty = true
    this.renderMessage("Alterações pendentes")
  }

  setupTextareaAutosize() {
    this.autosizeFields = Array.from(this.element.querySelectorAll("textarea[data-autosize='true']"))
    this.autosizeFields.forEach((field) => {
      field.addEventListener("input", this.boundAutosizeInput)
      this.autosizeField({ target: field })
    })
  }

  teardownTextareaAutosize() {
    if (!this.autosizeFields) return

    this.autosizeFields.forEach((field) => {
      field.removeEventListener("input", this.boundAutosizeInput)
    })
    this.autosizeFields = []
  }

  autosizeField(event) {
    const field = event?.target
    if (!(field instanceof HTMLTextAreaElement)) return

    field.style.height = "auto"
    field.style.overflowY = "hidden"
    field.style.height = `${field.scrollHeight}px`
  }

  // Usado nos campos de texto: salva automaticamente 1s após o usuário parar de digitar.
  // Adicione ao data-action dos textareas: "input->consultoria-autosave#markDirtyAndScheduleSave"
  markDirtyAndScheduleSave() {
    this.markDirty()
    clearTimeout(this._autosaveTimer)
    this._autosaveTimer = setTimeout(() => {
      if (this.dirty && !this.pending) this.save()
    }, this.debounceMsValue)
  }

  async save(event) {
    if (event) event.preventDefault()
    if (this.pending) return

    this.pending = true
    this.updateSaveButton(true)
    const url = this.normalizedUrl(this.urlValue)
    if (!url) {
      this.renderMessage("Erro ao salvar alterações")
      window.alert("Não foi possível salvar as alterações. Tente novamente.")
      this.pending = false
      this.updateSaveButton(false)
      return
    }

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          // Negocia Turbo Stream primeiro; cai em JSON se o servidor não suportar
          "Accept": "text/vnd.turbo-stream.html, application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify(this.buildPayload())
      })

      if (!response.ok) {
        let errorMessage = "Falha ao salvar atualizações"
        try {
          const payload = await response.json()
          if (Array.isArray(payload?.errors) && payload.errors.length > 0) {
            errorMessage = payload.errors.join(" ")
          }
        } catch (_parseError) {
          // noop: fallback to generic message
        }
        throw new Error(errorMessage)
      }

      const contentType = response.headers.get("Content-Type") || ""

      if (contentType.includes("turbo-stream")) {
        // Turbo renderiza automaticamente os partials (status badge + mensagem)
        const html = await response.text()
        window.Turbo.renderStreamMessage(html)
      } else {
        // Fallback JSON: atualiza a mensagem manualmente
        this.renderMessage("Atualizações salvas com sucesso")
      }

      this.dirty = false
    } catch (error) {
      this.renderMessage("Erro ao salvar alterações")
      window.alert(error?.message || "Não foi possível salvar as alterações. Tente novamente.")
    } finally {
      this.pending = false
      this.updateSaveButton(false)
    }
  }

  beforeUnload(event) {
    if (!this.shouldWarn()) return

    event.preventDefault()
    event.returnValue = ""
  }

  beforeVisit(event) {
    if (!this.shouldWarn()) return

    const canLeave = window.confirm("Os campos foram alterados. Sair sem salvar?")
    if (!canLeave) event.preventDefault()
  }

  clickCapture(event) {
    if (!this.shouldWarn()) return
    if (event.defaultPrevented) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    if (event.button && event.button !== 0) return

    const link = event.target.closest("a[href]")
    if (!link) return
    if (link.target === "_blank") return
    if (link.hasAttribute("download")) return
    if (link.dataset.skipUnsavedWarning === "true") return

    const href = link.getAttribute("href") || ""
    if (href.startsWith("#") || href.startsWith("javascript:")) return

    const canLeave = window.confirm("Os campos foram alterados. Sair sem salvar?")
    if (canLeave) return

    event.preventDefault()
    event.stopPropagation()
  }

  buildPayload() {
    const questionComments = {}
    this.element.querySelectorAll("textarea[data-question-id]").forEach((field) => {
      questionComments[field.dataset.questionId] = field.value
    })

    return {
      status: this.hasStatusTarget ? this.statusTarget.value : null,
      consultoria: {
        question_comments: questionComments,
        insight_analise: this.element.querySelector("textarea[data-final='insight_analise']")?.value || "",
        relatorio_pre_respostas: this.element.querySelector("textarea[data-final='relatorio_pre_respostas']")?.value || "",
        conclusao_geral: this.element.querySelector("textarea[data-final='conclusao']")?.value || "",
        plano_acao: this.element.querySelector("textarea[data-final='plano_acao']")?.value || "",
        responsavel_tecnico: this.element.querySelector("input[data-final='responsavel']")?.value || ""
      }
    }
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  updateSaveButton(loading) {
    if (!this.hasSaveButtonTarget) return

    this.saveButtonTarget.disabled = loading
    const text = loading ? "Salvando..." : "Salvar atualizações"
    if (Object.prototype.hasOwnProperty.call(this.saveButtonTarget, "value")) {
      this.saveButtonTarget.value = text
    }
    this.saveButtonTarget.textContent = text
  }

  renderMessage(text) {
    if (!this.hasMessageTarget) return

    this.messageTarget.textContent = text
  }

  shouldWarn() {
    return this.dirty && !this.pending
  }

  normalizedUrl(rawUrl) {
    if (!rawUrl) return ""
    return String(rawUrl).trim().replace(/^"+|"+$/g, "")
  }
}
